import React, { useEffect, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { GET_REVENUE_BY_ROOM_TYPE_CHART_DATA_API } from "../../../../services/ApiCalls";
import { useTranslation } from "react-i18next";

const PerformanceRevenue = () => {
  const { t } = useTranslation();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dateError, setDateError] = useState(false);

  const monthlyData = data?.monthlyData || [];
  const monthsWithData = monthlyData
    .filter((m) => m?.roomTypes && Object.keys(m.roomTypes).length > 0)
    .map((m) => m.month);

  const [selectedMonth, setSelectedMonth] = useState(
    monthsWithData.length ? monthsWithData[monthsWithData.length - 1] : null
  );

  const fetchAgedistributionGraphData = async ({
    propStart = "",
    propEnd = ""
  }) => {
    setLoading(true);
    try {
      const payload = {
        startDate: propStart || undefined,
        endDate: propEnd || undefined,
      };

      const res = await GET_REVENUE_BY_ROOM_TYPE_CHART_DATA_API(payload);
      if (res?.data?.status === "success") {
        setData(res?.data?.data?.revenueByRoomTypeAndDuration || {});
      }
    } catch (err) {
      console.error("API error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCallApiOnDateChange = () => {
    if (startDate && endDate) {
      if (new Date(startDate) > new Date(endDate)) {
        setDateError(true);
        toast.error(t("performanceRevenue.errors.invalidDateRange"));
        return;
      }
      setDateError(false);
      fetchAgedistributionGraphData({
        propStart: startDate,
        propEnd: endDate
      });
    }
  };

  const handleClearDates = () => {
    setDateError(false);
    setStartDate("");
    setEndDate("");
    fetchAgedistributionGraphData({
      propStart: "",
      propEnd: ""
    });
  };

  useEffect(() => {
    fetchAgedistributionGraphData({
      propStart: "",
      propEnd: ""
    });
  }, []);

  const { categories, series, isEmpty } = useMemo(() => {
    const activeMonthData = [...monthlyData]
      .reverse()
      .find((m) => m.month === selectedMonth);

    const durations = [
      "2hour",
      "3hour",
      "4hour",
      "5hour",
      "6hour",
      "12hour",
      "overnight",
      "unknown",
    ];

    if (!activeMonthData || !activeMonthData.roomTypes) {
      return {
        categories: durations,
        series: [],
        isEmpty: true,
      };
    }

    const roomTypes = Object.keys(activeMonthData.roomTypes);

    const series = roomTypes.map((roomType) => {
      const typeData = activeMonthData.roomTypes[roomType];
      const values = durations.map((d) => typeData?.[d]?.revenue || 0);
      return { name: roomType, data: values };
    });

    const allZero = series.every((s) => s.data.every((v) => v === 0));

    return {
      categories: durations.map((d) => t(`performanceRevenue.durations.${d}`)),
      series,
      isEmpty: allZero,
    };
  }, [data, selectedMonth, t]);

  const options = {
    chart: {
      type: "bar",
      height: 350,
      stacked: true,
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        borderRadius: 6,
        columnWidth: "50%",
      },
    },
    colors: ["#009298", "#e8307a", "#003e70", "#71da61", "#f3b700"],
    xaxis: {
      categories,
      title: { text: t("performanceRevenue.bookingDuration") },
    },
    yaxis: {
      title: { text: t("performanceRevenue.revenue") },
      labels: { formatter: (val) => `$${val.toLocaleString()}` },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: { formatter: (val) => `$${val.toLocaleString()}` },
    },
    legend: { position: "bottom" },
    dataLabels: { enabled: false },
  };

  useEffect(() => {
    if (monthsWithData.length > 0 && !selectedMonth) {
      setSelectedMonth(monthsWithData[monthsWithData.length - 1]);
    }
  }, [monthsWithData, selectedMonth]);

  return (
    <>
      <div className="d-flex align-items-end gap-3 mb-3">
        <div style={{ width: "200px" }}>
          <label className="small fw-semibold">
            {t("performanceRevenue.startDate")}
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className={`form-control ${dateError ? "border-danger" : ""}`}
          />
        </div>

        <div style={{ width: "200px" }}>
          <label className="small fw-semibold">
            {t("performanceRevenue.endDate")}
          </label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className={`form-control ${dateError ? "border-danger" : ""}`}
          />
        </div>
      </div>

      <div className="d-flex justify-content-end align-items-end gap-3 mb-3">
        {(startDate || endDate) && (
          <button className="btn btn-primary px-4" onClick={handleClearDates}>
            {t("performanceRevenue.clear")}
          </button>
        )}

        <button
          className="btn btn-primary px-4"
          onClick={handleCallApiOnDateChange}
          disabled={!startDate || !endDate || loading}
        >
          {loading ? t("performanceRevenue.loading") : t("performanceRevenue.apply")}
        </button>
      </div>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-2">
          {/* <h6 className="fw-bold" style={{ color: "#333" }}>
          Revenue by Room Type & Duration
        </h6> */}

          {/* 🔹 Month Selector Dropdown */}
          {monthsWithData.length > 1 && (
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="form-select form-select-sm w-auto"
            >
              {monthsWithData.map((month) => (
                <option key={month} value={month}>
                  {month}
                </option>
              ))}
            </select>
          )}
        </div>
        {selectedMonth && (
          <p className="text-secondary text-center fw-semibold mt-2 mb-0">
            {t("performanceRevenue.monthRevenue", { month: selectedMonth })}
          </p>
        )}

        <ReactApexChart
          key={selectedMonth + (series?.flat?.().join("-") || "")}
          options={options}
          series={isEmpty ? [] : series}
          type="bar"
          height={320}
        />

        {isEmpty && (
          <div className="text-center text-muted mt-2" style={{ fontSize: "14px" }}>
            {t("performanceRevenue.noData", { month: selectedMonth })}
          </div>
        )}
      </div>
    </>
  );
};

export default PerformanceRevenue;
